// src/pages/admin/VendorManagement/VendorList.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Eye, FileText, Store, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { adminAPI } from '../../../api/admin';
import { formatDateEnglish } from '../../../utils/date';
import { useDebounce } from '../../../hooks/useDebounce';
import type { Vendor } from '../../../types';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../../../components/common/StatusBadge';

const VendorList: React.FC = () => {
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [statusFilter, setStatusFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [tooltipStore, setTooltipStore] = useState<{ vendorId: number; x: number; y: number } | null>(null);
    const tooltipTimeout = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        total: 0,
        pages: 0,
        pageSize: 20,
    });

    useEffect(() => {
        fetchVendors();
    }, [debouncedSearch, statusFilter, pagination.page]);

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const params: any = {
                page: pagination.page,
                page_size: pagination.pageSize,
            };
            if (searchTerm) params.search = searchTerm;
            if (statusFilter !== 'all') params.status = statusFilter;

            const response = await adminAPI.getVendors(params);
            setVendors(response.data.results || response.data);
            setPagination({
                page: response.data.current_page || pagination.page,
                total: response.data.count || 0,
                pages: response.data.total_pages || 0,
                pageSize: pagination.pageSize,
            });
        } catch (error) {
            console.error('Failed to fetch vendors:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVendorClick = (vendor: Vendor) => {
        navigate(`/dashboard/vendors/${vendor.id}`);
    };

    const handleStoreHover = (e: React.MouseEvent, vendor: Vendor) => {
        if (!vendor.stores || vendor.stores.length <= 2) return;

        if (tooltipTimeout.current) {
            clearTimeout(tooltipTimeout.current);
        }

        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipStore({
            vendorId: vendor.id,
            x: rect.left,
            y: rect.bottom + 8,
        });
    };

    const handleStoreLeave = () => {
        tooltipTimeout.current = setTimeout(() => {
            setTooltipStore(null);
        }, 300);
    };

    const renderStores = (vendor: Vendor) => {
        if (!vendor.stores || vendor.stores.length === 0) {
            return <span className="text-gray-400 text-sm">—</span>;
        }

        const visibleStores = vendor.stores.slice(0, 2);
        const remainingCount = vendor.stores.length - 2;

        return (
            <div
                className="flex items-center gap-1"
                onMouseEnter={(e) => handleStoreHover(e, vendor)}
                onMouseLeave={handleStoreLeave}
            >
                <Store className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                    {visibleStores.map(s => s.name).join(', ')}
                    {remainingCount > 0 && (
                        <span className="text-gray-400 text-xs ml-0.5">
                            +{remainingCount} more
                        </span>
                    )}
                </span>
            </div>
        );
    };

    const statusOptions = [
        { value: 'all', label: 'All' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
    ];

    const getVendorStatusBadge = (status: string, isActive: boolean) => {
        if (!isActive) return { label: 'Inactive', className: 'bg-gray-100 text-gray-600' };
        switch (status) {
            case 'approved': return { label: 'Approved', className: 'bg-green-100 text-green-700' };
            case 'pending': return { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' };
            case 'rejected': return { label: 'Rejected', className: 'bg-red-100 text-red-700' };
            default: return { label: 'Unknown', className: 'bg-gray-100 text-gray-600' };
        }
    };

    const getStatusIcon = (status: string, isActive: boolean) => {
        if (!isActive) return <span className="w-2 h-2 rounded-full bg-gray-400" />;
        switch (status) {
            case 'approved': return <span className="w-2 h-2 rounded-full bg-green-500" />;
            case 'pending': return <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />;
            case 'rejected': return <span className="w-2 h-2 rounded-full bg-red-500" />;
            default: return <span className="w-2 h-2 rounded-full bg-gray-400" />;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-sm text-gray-500">Manage all vendor accounts and their stores</p>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500 border-l border-gray-300 pl-3">
                        <span className="font-medium text-gray-700">
                            {vendors.filter(v => v.verification_status === 'pending').length}
                        </span> pending
                    </span>
                </div>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by business name, email, or contact..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    <Filter className="h-4 w-4" />
                    {statusFilter !== 'all' && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    {statusOptions.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => {
                                setStatusFilter(opt.value);
                                setShowFilters(false);
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === opt.value
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : vendors.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <Store className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No vendors found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                </div>
            ) : (
                <>
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Active</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Business</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Contact</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Stores</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Products</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Verification</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Joined</th>
                                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {vendors.map((vendor) => {
                                        const status = getVendorStatusBadge(vendor.verification_status, vendor.is_active);
                                        const statusIcon = getStatusIcon(vendor.verification_status, vendor.is_active);

                                        return (
                                            <tr
                                                key={vendor.id}
                                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                                onClick={() => handleVendorClick(vendor)}
                                            >
                                                {/* Active Column */}
                                                <td className="px-4 py-3">
                                                    <StatusBadge isActive={vendor.is_active} size="sm" />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{vendor.business_name}</p>
                                                        <p className="text-xs text-gray-500">{vendor.user?.first_name} {vendor.user?.last_name}</p>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="text-sm text-gray-600">
                                                        <p>{vendor.business_email}</p>
                                                        <p className="text-xs text-gray-400">{formatPhoneNumber(vendor.business_phone) || 'N/A'}</p>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-sm text-gray-600 capitalize">
                                                        {vendor.vendor_type || 'Individual'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {renderStores(vendor)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {vendor.product_count || 0}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.className}`}>
                                                        {statusIcon}
                                                        {status.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {formatDateEnglish(vendor.created_at)}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    {vendor.verification_status === 'pending' ? (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/dashboard/vendor-applications/${vendor.id}`);
                                                            }}
                                                            className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 ml-auto p-1"
                                                        >
                                                            Review
                                                            <FileText className="h-4 w-4" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/dashboard/vendors/${vendor.id}`);
                                                            }}
                                                            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 ml-auto p-1"
                                                        >
                                                            View
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {/* Results count */}
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
                            Showing {vendors.length} vendor {vendors.length !== 1 ? 's' : ''}
                            {searchTerm ? `matching "${searchTerm}"` : ''}
                            of {pagination.total} total
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-500">
                                Showing {vendors.length} of {pagination.total} vendors
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={pagination.page <= 1}
                                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="text-sm text-gray-600">
                                    Page {pagination.page} of {pagination.pages}
                                </span>
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={pagination.page >= pagination.pages}
                                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Store Tooltip */}
            {tooltipStore && (
                <div
                    className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg p-2 max-w-xs shadow-lg"
                    style={{ left: tooltipStore.x, top: tooltipStore.y }}
                    onMouseEnter={() => {
                        if (tooltipTimeout.current) {
                            clearTimeout(tooltipTimeout.current);
                            tooltipTimeout.current = null;
                        }
                    }}
                    onMouseLeave={handleStoreLeave}
                >
                    {vendors.find(v => v.id === tooltipStore.vendorId)?.stores.map(s => s.name).join('\n')}
                </div>
            )}
        </div>
    );
};

export default VendorList;